import { useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  "Supported Living",
  "Residential Care",
  "Domiciliary Care",
  "Multiple Services",
  "Other",
];

const field =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all";

export default function DemoForm() {
  const [form, setForm] = useState({
    name: "", organisation: "", work_email: "", telephone: "",
    service_type: "", locations: "", service_users: "", message: "", consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organisation || !form.work_email || !form.service_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please accept the privacy consent to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/demo-request`, form);
      setDone(true);
      toast.success("Thanks — your demo request has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="demo-form-success" className="rounded-3xl bg-white border border-slate-200 p-10 text-center shadow-xl">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 font-display">Request received</h3>
        <p className="mt-2 text-slate-600 max-w-md mx-auto">
          Thank you, {form.name.split(" ")[0]}. A member of the AYOBELL team will be in touch shortly to arrange your personalised demo.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="demo-form"
      className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-xl"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Name *</label>
          <input data-testid="demo-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Okafor" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Organisation *</label>
          <input data-testid="demo-organisation" className={field} value={form.organisation} onChange={set("organisation")} placeholder="Willow Care Group" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Work email *</label>
          <input data-testid="demo-email" type="email" className={field} value={form.work_email} onChange={set("work_email")} placeholder="jane@willowcare.co.uk" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Telephone</label>
          <input data-testid="demo-telephone" className={field} value={form.telephone} onChange={set("telephone")} placeholder="020 7000 0000" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Type of service *</label>
          <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
            <SelectTrigger data-testid="demo-service-type" className={`${field} flex items-center justify-between h-auto`}>
              <SelectValue placeholder="Select a service type" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_TYPES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`demo-service-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Number of locations</label>
          <input data-testid="demo-locations" className={field} value={form.locations} onChange={set("locations")} placeholder="e.g. 3" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Approximate number of service users</label>
          <input data-testid="demo-service-users" className={field} value={form.service_users} onChange={set("service_users")} placeholder="e.g. 40" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Message</label>
          <textarea data-testid="demo-message" rows={3} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your service and what you'd like to see." />
        </div>
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input
          data-testid="demo-consent"
          type="checkbox"
          checked={form.consent}
          onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
        />
        <span className="text-xs text-slate-500 leading-relaxed">
          I consent to AYOBELL contacting me about my enquiry and processing my details in line with the Privacy Policy. We will never share your information with third parties.
        </span>
      </label>

      <button
        type="submit"
        data-testid="demo-form-submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-4 rounded-full hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request My Demo <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}
